/**
 * Build the PATCH body that links a journal to a sale.
 *
 * Linking ADDS the journal to the target sale's embedded journals array (or
 * replaces it in place when the sale already embeds it) — it never removes
 * the journal from any other sale. One journal may be linked to several
 * sales (confirmed design). The journal's animal is appended to the sale's
 * animals when missing, so the sale view can show it.
 *
 * The backend whitelists 'journals-overwrite' / 'animals-overwrite' for
 * exactly this (backend/app/routes/api/sales/patch-sale.js).
 *
 * @param {Object} props
 * @param {Object} props.targetSale - The sale to link the journal to (with embedded journals/animals).
 * @param {Object} props.journal - The journal object to embed.
 * @param {Object} [props.animal] - The journal's animal; appended when missing on the sale.
 * @returns {Object} PATCH body for /api/sales/:id
 */
export function buildLinkJournalPatchBody({ targetSale, journal, animal }) {
    const existingJournals = targetSale.journals || [];
    const existingAnimals = targetSale.animals || [];

    const existingIdx = existingJournals.findIndex(j => j.id === journal.id);
    let updatedJournals;
    if (existingIdx > -1) {
        updatedJournals = [...existingJournals];
        updatedJournals[existingIdx] = journal;
    } else {
        updatedJournals = [...existingJournals, journal];
    }

    const patchBody = {
        id: targetSale.id,
        'journals-overwrite': updatedJournals,
    };
    if (animal && animal.id && !existingAnimals.find(a => a.id === animal.id)) {
        patchBody['animals-overwrite'] = [...existingAnimals, animal];
    }
    return patchBody;
}

/**
 * Build the PATCH body that removes a journal's link to a sale.
 *
 * The counterpart of buildLinkJournalPatchBody: replaces the sale's embedded
 * journals array with a copy that excludes the journal. The sale's animals
 * are left untouched — the animal may be on the sale for other reasons
 * (other journals, product lines).
 *
 * @param {Object} props
 * @param {Object} props.targetSale - The sale to unlink the journal from (with embedded journals).
 * @param {Object} props.journal - The journal to remove.
 * @returns {Object} PATCH body for /api/sales/:id
 */
export function buildUnlinkJournalPatchBody({ targetSale, journal }) {
    const existingJournals = targetSale.journals || [];
    return {
        id: targetSale.id,
        'journals-overwrite': existingJournals.filter(j => j.id !== journal.id),
    };
}
